import React, { useState, useEffect, memo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Star, ChevronLeft, ChevronRight } from 'lucide-react';

const TestimonialsSection = memo(({ testimonials }) => {
  const navigate = useNavigate();
  const [currentTestimonial, setCurrentTestimonial] = useState(0);
  const [isTestimonialHovered, setIsTestimonialHovered] = useState(false);

  useEffect(() => {
    if (!isTestimonialHovered && testimonials.length > 0) {
      const interval = setInterval(() => {
        setCurrentTestimonial((prev) => (prev + 1) % testimonials.length);
      }, 5000);
      return () => clearInterval(interval);
    }
  }, [isTestimonialHovered, testimonials.length]);

  const nextTestimonial = () => {
    setCurrentTestimonial((prev) => (prev + 1) % testimonials.length);
  };

  const prevTestimonial = () => {
    setCurrentTestimonial((prev) => (prev - 1 + testimonials.length) % testimonials.length);
  };

  if (testimonials.length === 0) return null;

  return (
    <section id="temoignages" className="testimonials" data-testid="testimonials-section">
      <div className="section-header">
        <h2 className="section-title" data-testid="testimonials-title">ILS NOUS FONT CONFIANCE</h2>
        <p className="section-subtitle">Ce que disent nos clients</p>
      </div>

      <div className="testimonial-container">
        <button 
          className="testimonial-nav testimonial-nav-left testimonial-nav-desktop" 
          onClick={prevTestimonial} 
          data-testid="testimonial-prev"
          aria-label="Témoignage précédent"
        >
          <ChevronLeft />
        </button>
        
        <div className="testimonial-wrapper">
          <div 
            className="testimonial-card" 
            data-testid="testimonial-card"
            onMouseEnter={() => setIsTestimonialHovered(true)}
            onMouseLeave={() => setIsTestimonialHovered(false)}
          >
            <div className="testimonial-stars">
              {[...Array(testimonials[currentTestimonial]?.rating || 5)].map((_, i) => (
                <Star key={i} className="star" fill="currentColor" />
              ))}
            </div>
            <p className="testimonial-text" data-testid="testimonial-text">
              "{testimonials[currentTestimonial]?.comment}"
            </p>
            <div className="testimonial-author" data-testid="testimonial-author">
              <div className="testimonial-name">{testimonials[currentTestimonial]?.name}</div>
              <div className="testimonial-event">{testimonials[currentTestimonial]?.event_type} - {testimonials[currentTestimonial]?.date}</div>
            </div>
          </div>
          
          <div className="testimonial-nav-mobile">
            <button className="testimonial-nav-btn-mobile" onClick={prevTestimonial} aria-label="Témoignage précédent">
              <ChevronLeft size={24} />
            </button>
            <button className="testimonial-nav-btn-mobile" onClick={nextTestimonial} aria-label="Témoignage suivant">
              <ChevronRight size={24} />
            </button>
          </div>
        </div>
        
        <button 
          className="testimonial-nav testimonial-nav-right testimonial-nav-desktop" 
          onClick={nextTestimonial} 
          data-testid="testimonial-next"
          aria-label="Témoignage suivant"
        >
          <ChevronRight />
        </button>
      </div>

      <div className="testimonial-dots">
        {testimonials.map((_, index) => (
          <button
            key={index}
            onClick={() => setCurrentTestimonial(index)}
            className={`testimonial-dot ${index === currentTestimonial ? 'active' : ''}`}
            data-testid={`testimonial-dot-${index}`}
            aria-label={`Aller au témoignage ${index + 1}`}
          />
        ))}
      </div>

      <div className="testimonial-cta">
        <button 
          onClick={() => navigate('/laisser-avis')} 
          className="leave-review-button"
          data-testid="leave-review-button"
        >
          <Star className="button-icon" />
          Laisser un Avis
        </button>
      </div>
    </section>
  );
});

TestimonialsSection.displayName = 'TestimonialsSection';

export default TestimonialsSection;
